import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LuClipboardList, LuFilterX, LuHospital, LuHouse, LuPlus, LuTableProperties } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { INPUT_CLASS, SelectInput } from '../../components/form/FormControls';
import HastaneDataEntryContent from './HastaneDataEntryContent';
import EntryGrid from './parts/EntryGrid';
import { homeEntryColumns } from './parts/entryColumns';
import { EntryCount, EntryEmpty, EntryFooterInfo, EntrySaveButton } from './parts/entryBits';
import { createBlankRow, rowToFields, visitToRow } from './parts/homeVisitRows';
import useEntryRows from './useEntryRows';
import useSmoText from './smoText';

// Native select (a project can appear twice, so SelectInput's value keys don't fit)
const SELECT = `${INPUT_CLASS} tw-cursor-pointer`;

const EMPTY_FILTERS = {
  projectId: '',
  patientId: '',
  visitNumber: '',
  windowOpenDateFrom: '', windowOpenDateTo: '',
  targetDateFrom: '', targetDateTo: '',
  windowCloseDateFrom: '', windowCloseDateTo: '',
  clinicianFullName: '',
  scheduledDateFrom: '', scheduledDateTo: '',
  visitStartDateFrom: '', visitStartDateTo: '',
  visitEndDateFrom: '', visitEndDateTo: '',
  issueOccurred: '',
  issueResultedInMiss: '',
  issueSource: '',
  smoCorrectionRequests: '',
  clinicalPMCorrections: '',
  correctionReason: '',
  dateInitialSourceReceivedFrom: '', dateInitialSourceReceivedTo: '',
  dateLatestSourceRevisionFrom: '', dateLatestSourceRevisionTo: '',
  dateSourceFinalizedFrom: '', dateSourceFinalizedTo: '',
  wasAeSaeTaken: '',
  wasDcfCreated: '',
};

const ManualDataEntryModal = ({ show, onHide, workers, selectedSMO, onDataSaved }) => {
  const { t, label, language } = useSmoText();
  // Toasts in callbacks read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const [activeTab, setActiveTab] = useState('home');
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [projectsMap, setProjectsMap] = useState({});
  const loadIdRef = useRef(0);
  const { rows, setLoaded, change, addRow, removeRow, pending, save, saving } = useEntryRows({
    labelRef, createBlankRow, toFields: rowToFields, onSaved: onDataSaved,
  });
  // Unsaved work of the Hastane tab, reported by it
  const [sitePending, setSitePending] = useState(false);
  // Action waiting for "discard unsaved changes?" (close, switch worker / project)
  const [discardAction, setDiscardAction] = useState(null);
  const hasUnsaved = pending.any || sitePending;
  const guardUnsaved = (action) => (hasUnsaved ? setDiscardAction(() => action) : action());

  // Load all projects once to map projectId → display title
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Projects'));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data() || {};
          map[d.id] = data.titleShort || data.titleLong || data.title || '';
        });
        setProjectsMap(map);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);
  const workerProjects = selectedWorker?.projects || [];

  // Extract unique patient IDs from rows for dropdown
  const uniquePatientIds = useMemo(() => {
    const patientIds = rows.map(row => row.patientId).filter(Boolean);
    return [...new Set(patientIds)].sort();
  }, [rows]);

  // Unique projects present in current rows (for filter dropdown)
  const availableRowProjects = useMemo(() => {
    const ids = [...new Set(rows.map(r => r.projectId).filter(Boolean))];
    return ids
      .map(id => ({ id, title: projectsMap[id] || id }))
      .sort((a, b) => a.title.localeCompare(b.title, 'tr'));
  }, [rows, projectsMap]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || selectedPatientId !== '';

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  };

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Load visits when employee is selected
  const loadVisits = useCallback(async () => {
    const myLoad = ++loadIdRef.current;
    if (!selectedWorkerId) { setLoaded([]); setLoading(false); return; }
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      // For home visits, only filter by employee and visit type (no project filter)
      const q = query(visitsRef, where('employeeId', '==', selectedWorkerId), where('visitType', '==', 'home'));
      const snap = await getDocs(q);
      // A newer load (another worker picked meanwhile) wins
      if (myLoad !== loadIdRef.current) return;
      const visitRows = snap.docs.map(d => visitToRow({ id: d.id, ...d.data() }));
      // Sort: oldest first (newest at bottom)
      visitRows.sort((a, b) => {
        const dateA = a.scheduledDate || '';
        const dateB = b.scheduledDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.scheduledTime || '';
        const timeB = b.scheduledTime || '';
        return timeA.localeCompare(timeB);
      });
      setLoaded(visitRows);
    } catch (err) {
      if (myLoad !== loadIdRef.current) return;
      console.error('Error loading visits:', err);
      toast.error(labelRef.current('entry.toast.loadError'));
    } finally {
      if (myLoad === loadIdRef.current) setLoading(false);
    }
  }, [selectedWorkerId, setLoaded]);

  useEffect(() => {
    if (show) loadVisits();
  }, [show, loadVisits]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setSelectedWorkerId('');
      setSelectedProjectId('');
      setSelectedPatientId('');
      setLoaded([]);
      setSitePending(false);
      setFilters(EMPTY_FILTERS);
    }
  }, [show, setLoaded]);

  // Reset filters when worker changes
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  }, [selectedWorkerId]);

  // Filtered rows (display only — rows state is source of truth for saving)
  const filteredRows = useMemo(() => {
    const dateInRange = (val, from, to) => {
      if (!from && !to) return true;
      if (!val) return false;
      if (from && val < from) return false;
      if (to && val > to) return false;
      return true;
    };

    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => {
        // Patient ID filter (from dropdown)
        if (selectedPatientId && row.patientId !== selectedPatientId) return false;

        // Project filter
        if (filters.projectId && row.projectId !== filters.projectId) return false;
        
        // Text filters
        if (filters.patientId && !row.patientId.toLowerCase().includes(filters.patientId.toLowerCase())) return false;
        if (filters.visitNumber && !String(row.visitNumber).toLowerCase().includes(filters.visitNumber.toLowerCase())) return false;
        if (filters.clinicianFullName && !row.clinicianFullName.toLowerCase().includes(filters.clinicianFullName.toLowerCase())) return false;
        if (filters.issueSource && !row.issueSource.toLowerCase().includes(filters.issueSource.toLowerCase())) return false;
        if (filters.smoCorrectionRequests && !row.smoCorrectionRequests.toLowerCase().includes(filters.smoCorrectionRequests.toLowerCase())) return false;
        if (filters.clinicalPMCorrections && !row.clinicalPMCorrections.toLowerCase().includes(filters.clinicalPMCorrections.toLowerCase())) return false;
        if (filters.correctionReason && !row.correctionReason.toLowerCase().includes(filters.correctionReason.toLowerCase())) return false;
        // Select filters
        if (filters.issueOccurred && row.issueOccurred !== filters.issueOccurred) return false;
        if (filters.issueResultedInMiss && !row.issueResultedInMiss.toLowerCase().includes(filters.issueResultedInMiss.toLowerCase())) return false;
        if (filters.wasAeSaeTaken && row.wasAeSaeTaken !== filters.wasAeSaeTaken) return false;
        if (filters.wasDcfCreated && row.wasDcfCreated !== filters.wasDcfCreated) return false;
        // Date range filters
        if (!dateInRange(row.windowOpenDate, filters.windowOpenDateFrom, filters.windowOpenDateTo)) return false;
        if (!dateInRange(row.targetDate, filters.targetDateFrom, filters.targetDateTo)) return false;
        if (!dateInRange(row.windowCloseDate, filters.windowCloseDateFrom, filters.windowCloseDateTo)) return false;
        if (!dateInRange(row.scheduledDate, filters.scheduledDateFrom, filters.scheduledDateTo)) return false;
        if (!dateInRange(row.visitStartDate, filters.visitStartDateFrom, filters.visitStartDateTo)) return false;
        if (!dateInRange(row.visitEndDate, filters.visitEndDateFrom, filters.visitEndDateTo)) return false;
        if (!dateInRange(row.dateInitialSourceReceived, filters.dateInitialSourceReceivedFrom, filters.dateInitialSourceReceivedTo)) return false;
        if (!dateInRange(row.dateLatestSourceRevision, filters.dateLatestSourceRevisionFrom, filters.dateLatestSourceRevisionTo)) return false;
        if (!dateInRange(row.dateSourceFinalized, filters.dateSourceFinalizedFrom, filters.dateSourceFinalizedTo)) return false;
        return true;
      });
  }, [rows, filters, selectedPatientId]);

  const handleSave = () => {
    if (!selectedWorkerId) { toast.warn(labelRef.current('entry.toast.pickWorker')); return; }
    const worker = workers.find(w => w.id === selectedWorkerId);
    save({ employeeId: selectedWorkerId, employeeName: worker ? `${worker.name} ${worker.surname}` : '' });
  };

  const requestClose = () => guardUnsaved(onHide);

  if (!show) return null;

  const existingCount = rows.filter(r => !r._isNew).length;
  const newCount = rows.filter(r => r._isNew).length;
  const { groups, columns } = homeEntryColumns({ label, projectsMap, availableRowProjects, change });

  return (
    <Modal
      isOpen={!!show}
      onClose={requestClose}
      size="full"
      title={label('entry.title')}
      subtitle={`${selectedSMO ? `${selectedSMO.name} ${selectedSMO.surname}` : 'SMO'} — ${label('entry.subtitle')}`}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-flex tw-flex-col tw-p-0"
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" size="xs">
          <LuTableProperties aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <div className="tw-mr-auto tw-min-w-0">
            {activeTab === 'home' && rows.length > 0 && (
              <EntryFooterInfo shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} changed={pending.changed} removed={pending.removed} />
            )}
          </div>
          <Button variant="ghost" onClick={requestClose} disabled={saving}>{label('entry.cancel')}</Button>
          {activeTab === 'home' && (
            <EntrySaveButton pending={pending} saving={saving} disabled={loading} onSave={handleSave} />
          )}
        </>
      )}
    >
      {/* Tabs + selectors */}
      <div className="tw-shrink-0 tw-space-y-3 tw-border-b tw-border-slate-200/80 tw-px-4 tw-py-3 sm:tw-px-6 dark:tw-border-white/10">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <SegmentedControl
            layoutId="smo-manual-entry-tab"
            label={label('entry.tabs.label')}
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'home', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuHouse aria-hidden="true" className="tw-h-4 tw-w-4" />{label('entry.tabs.home')}</span> },
              { value: 'hastane', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuHospital aria-hidden="true" className="tw-h-4 tw-w-4" />{label('entry.tabs.site')}</span> },
            ]}
          />
          <p className="tw-min-w-0 tw-flex-1 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('entry.help')}</p>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <label className="tw-block tw-w-full sm:tw-w-56">
            <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.worker')}</span>
            <SelectInput
              value={selectedWorkerId}
              onChange={(e) => { const next = e.target.value; guardUnsaved(() => setSelectedWorkerId(next)); }}
              disabled={saving}
              placeholder={label('entry.chooseWorker')}
              options={workers.map(w => ({ value: w.id, label: `${w.name} ${w.surname}` }))}
            />
          </label>

          {activeTab === 'hastane' && selectedWorker && workerProjects.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-56">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.project')}</span>
              <select
                value={selectedProjectId}
                onChange={(e) => { const next = e.target.value; guardUnsaved(() => setSelectedProjectId(next)); }}
                className={SELECT}
              >
                <option value="">{label('entry.allProjects')}</option>
                {workerProjects.map((p, i) => (
                  // Aynı proje birden fazla atamayla gelebilir
                  // eslint-disable-next-line react/no-array-index-key
                  <option key={`${p.projectId}-${i}`} value={p.projectId}>{p.projectTitle}</option>
                ))}
              </select>
            </label>
          )}

          {activeTab === 'home' && selectedWorkerId && availableRowProjects.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-56">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.project')}</span>
              <SelectInput
                value={filters.projectId}
                onChange={e => setFilter('projectId', e.target.value)}
                placeholder={label('entry.allProjects')}
                options={availableRowProjects.map(p => ({ value: p.id, label: p.title }))}
              />
            </label>
          )}

          {activeTab === 'home' && selectedWorkerId && uniquePatientIds.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-44">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.patient')}</span>
              <SelectInput
                value={selectedPatientId}
                onChange={e => setSelectedPatientId(e.target.value)}
                placeholder={label('entry.allPatients')}
                options={uniquePatientIds.map(patientId => ({ value: patientId, label: patientId }))}
              />
            </label>
          )}

          {activeTab === 'home' && rows.length > 0 && (
            <EntryCount shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} />
          )}

          {activeTab === 'home' && hasActiveFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} title={label('entry.clearFiltersHint')}>
              <LuFilterX aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.clearFilters')}
            </Button>
          )}

          {activeTab === 'home' && selectedWorkerId && (
            <Button size="sm" variant="success" onClick={addRow} disabled={saving || loading}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.addRow')}
            </Button>
          )}
        </div>
      </div>

      {/* Hastane Tab Content: stays mounted so switching tabs keeps its unsaved edits */}
      <div className={activeTab === 'hastane' ? 'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col' : 'tw-hidden'}>
        <HastaneDataEntryContent
          workers={workers}
          selectedSMO={selectedSMO}
          selectedWorkerId={selectedWorkerId}
          selectedProjectId={selectedProjectId}
          projectsMap={projectsMap}
          onDataSaved={onDataSaved}
          onPendingChange={setSitePending}
        />
      </div>

      {/* Home Tab Table */}
      {activeTab === 'home' && (
        <div className="tw-min-h-[16rem] tw-flex-1 tw-overflow-auto glass-scrollbar">
          {loading ? (
            <EntryEmpty loading text={label('entry.loading')} />
          ) : !selectedWorkerId ? (
            <EntryEmpty icon={LuClipboardList} text={label('entry.pickWorker')} />
          ) : rows.length === 0 ? (
            <EntryEmpty icon={LuClipboardList} text={label('entry.noHomeVisits')} actionLabel={label('entry.addRow')} onAction={addRow} />
          ) : (
            <EntryGrid
              groups={groups}
              columns={columns}
              rows={filteredRows}
              showFilters={showFilters}
              toggleFilters={toggleFilters}
              filters={filters}
              setFilter={setFilter}
              onRemove={removeRow}
              locked={saving}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={discardAction !== null}
        title={label('entry.unsaved.title')}
        message={label('entry.unsaved.message')}
        confirmLabel={label('entry.unsaved.discard')}
        cancelLabel={label('entry.unsaved.stay')}
        onCancel={() => setDiscardAction(null)}
        onConfirm={() => { const action = discardAction; setDiscardAction(null); action(); }}
      />
    </Modal>
  );
};

ManualDataEntryModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  workers: PropTypes.array.isRequired,
  selectedSMO: PropTypes.object,
  onDataSaved: PropTypes.func,
};

export default ManualDataEntryModal;
